import React, { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from 'notistack';
import {
  Box,
  Typography,
  Button,
  Chip,
  IconButton,
  Tooltip,
  TextField,
  Stack,
  Alert,
  CircularProgress,
  _alpha,
  useTheme,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Avatar,
  Divider,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Search as SearchIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  Person as PersonIcon,
  Business as BusinessIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  LocationOn as LocationIcon,
  AttachMoney as MoneyIcon,
  Assignment as AssignmentIcon,
} from '@mui/icons-material';
import {
  useCustomers,
  useCustomer,
  useCreateCustomer,
  useUpdateCustomer,
  useDeleteCustomer,
} from '../../hooks/useCustomers';
import {
  useCustomerPricingGroup,
  useAssignCustomerToPricingGroup,
  usePricingGroups,
} from '../../hooks/useBilling';
import { handleApiError } from '../../utils/errorHandler';

const CustomerManagementPanel = () => {
  const { t } = useTranslation('business');
  const theme = useTheme();
  const { enqueueSnackbar } = useSnackbar();

  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogMode, setDialogMode] = useState('create');
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [assignPricingOpen, setAssignPricingOpen] = useState(false);
  const [selectedPricingGroup, setSelectedPricingGroup] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    address: '',
    city: '',
    country: '',
    status: 'active',
  });

  const { data: customersData, isLoading: customersLoading, refetch: refetchCustomers } = useCustomers();
  const { data: _customerDetail, isLoading: customerDetailLoading } = useCustomer(selectedCustomer?.id);
  const { data: customerPricingGroup } = useCustomerPricingGroup(selectedCustomer?.id);
  const { data: pricingGroupsData } = usePricingGroups();

  const createCustomer = useCreateCustomer();
  const updateCustomer = useUpdateCustomer();
  const deleteCustomer = useDeleteCustomer();
  const assignPricingGroup = useAssignCustomerToPricingGroup();

  const customers = useMemo(() => {
    // Ensure customers is always an array
    if (Array.isArray(customersData?.items)) return customersData.items;
    if (Array.isArray(customersData)) return customersData;
    return [];
  }, [customersData]);
  const pricingGroups = useMemo(() => {
    // Ensure pricingGroups is always an array
    if (Array.isArray(pricingGroupsData?.items)) return pricingGroupsData.items;
    if (Array.isArray(pricingGroupsData)) return pricingGroupsData;
    return [];
  }, [pricingGroupsData]);

  const filteredCustomers = useMemo(() => {
    // Ensure we're working with an array
    if (!Array.isArray(customers)) return [];
    let filtered = customers;
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(customer =>
        customer.name?.toLowerCase().includes(query) ||
        customer.email?.toLowerCase().includes(query) ||
        customer.company?.toLowerCase().includes(query)
      );
    }
    if (statusFilter) {
      filtered = filtered.filter(customer => customer.status === statusFilter);
    }
    return filtered;
  }, [customers, searchQuery, statusFilter]);

  const handleOpenDialog = (mode, customer = null) => {
    setDialogMode(mode);
    if (customer) {
      setFormData({
        name: customer.name || '',
        email: customer.email || '',
        phone: customer.phone || '',
        company: customer.company || '',
        address: customer.address || '',
        city: customer.city || '',
        country: customer.country || '',
        status: customer.status || 'active',
      });
    } else {
      setFormData({
        name: '',
        email: '',
        phone: '',
        company: '',
        address: '',
        city: '',
        country: '',
        status: 'active',
      });
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setFormData({
      name: '',
      email: '',
      phone: '',
      company: '',
      address: '',
      city: '',
      country: '',
      status: 'active',
    });
  };

  const handleSubmit = async () => {
    try {
      if (dialogMode === 'create') {
        await createCustomer.mutateAsync(formData);
        enqueueSnackbar(t('customers.messages.createSuccess', 'Customer created successfully'), { variant: 'success' });
      } else {
        await updateCustomer.mutateAsync({ customerId: selectedCustomer.id, data: formData });
        enqueueSnackbar(t('customers.messages.updateSuccess', 'Customer updated successfully'), { variant: 'success' });
      }
      handleCloseDialog();
      refetchCustomers();
    } catch (error) {
      handleApiError(error, enqueueSnackbar);
    }
  };

  const handleDelete = async () => {
    try {
      await deleteCustomer.mutateAsync(selectedCustomer.id);
      enqueueSnackbar(t('customers.messages.deleteSuccess', 'Customer deleted successfully'), { variant: 'success' });
      setDeleteConfirmOpen(false);
      setSelectedCustomer(null);
      refetchCustomers();
    } catch (error) {
      handleApiError(error, enqueueSnackbar);
    }
  };

  const handleAssignPricingGroup = async () => {
    try {
      await assignPricingGroup.mutateAsync({ 
        customerId: selectedCustomer.id, 
        groupId: selectedPricingGroup 
      });
      enqueueSnackbar(t('customers.messages.pricingAssignSuccess', 'Pricing group assigned successfully'), { variant: 'success' });
      setAssignPricingOpen(false);
      setSelectedPricingGroup('');
    } catch (error) {
      handleApiError(error, enqueueSnackbar);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      active: 'success',
      inactive: 'default',
      suspended: 'error',
      pending: 'warning',
    };
    return colors[status] || 'default';
  };

  const _customerColumns = [
    { field: 'id', headerName: 'ID', width: 80 },
    { 
      field: 'name', 
      headerName: t('customers.columns.name', 'Name'), 
      flex: 1,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Avatar sx={{ width: 32, height: 32, bgcolor: theme.palette.primary.main }}>
            {params.value?.charAt(0) || '?'}
          </Avatar>
          <Typography variant="body2">{params.value}</Typography>
        </Box>
      ),
    },
    { field: 'email', headerName: t('customers.columns.email', 'Email'), flex: 1 },
    { field: 'company', headerName: t('customers.columns.company', 'Company'), flex: 1 },
    { 
      field: 'status', 
      headerName: t('customers.columns.status', 'Status'), 
      width: 120,
      renderCell: (params) => (
        <Chip 
          label={t(`customers.status.${params.value}`, params.value)} 
          size="small" 
          color={getStatusColor(params.value)}
        />
      ),
    },
    {
      field: 'actions',
      headerName: t('customers.columns.actions', 'Actions'),
      width: 150,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          <Tooltip title={t('common.view', 'View')}>
            <IconButton size="small" onClick={() => setSelectedCustomer(params.row)}>
              <ViewIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title={t('common.edit', 'Edit')}>
            <IconButton size="small" onClick={() => { setSelectedCustomer(params.row); handleOpenDialog('edit', params.row); }}>
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title={t('common.delete', 'Delete')}>
            <IconButton size="small" color="error" onClick={() => { setSelectedCustomer(params.row); setDeleteConfirmOpen(true); }}>
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ];

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box>
          <Typography variant="h6">{t('customers.title', 'Customer Management')}</Typography>
          <Typography variant="body2" color="text.secondary">{t('customers.subtitle', 'Manage customer accounts and pricing')}</Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <TextField
            size="small"
            placeholder={t('customers.searchPlaceholder', 'Search customers...')}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />,
            }}
          />
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>{t('customers.filterByStatus', 'Status')}</InputLabel>
            <Select
              value={statusFilter}
              label={t('customers.filterByStatus', 'Status')}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <MenuItem value="">{t('common.all', 'All')}</MenuItem>
              <MenuItem value="active">{t('customers.status.active', 'Active')}</MenuItem>
              <MenuItem value="inactive">{t('customers.status.inactive', 'Inactive')}</MenuItem>
              <MenuItem value="suspended">{t('customers.status.suspended', 'Suspended')}</MenuItem>
              <MenuItem value="pending">{t('customers.status.pending', 'Pending')}</MenuItem>
            </Select>
          </FormControl>
          <Button variant="outlined" startIcon={<RefreshIcon />} onClick={() => refetchCustomers()}>
            {t('common.refresh', 'Refresh')}
          </Button>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleOpenDialog('create')}>
            {t('customers.addCustomer', 'Add Customer')}
          </Button>
        </Box>
      </Box>

      <Box sx={{ flexGrow: 1, display: 'flex' }}>
        <Box sx={{ width: selectedCustomer ? '60%' : '100%', borderRight: selectedCustomer ? 1 : 0, borderColor: 'divider' }}>
          {customersLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 300 }}>
              <CircularProgress />
            </Box>
          ) : (
            <TableContainer component={Paper}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>ID</TableCell>
                    <TableCell>{t('customers.columns.name', 'Name')}</TableCell>
                    <TableCell>{t('customers.columns.email', 'Email')}</TableCell>
                    <TableCell>{t('customers.columns.company', 'Company')}</TableCell>
                    <TableCell>{t('customers.columns.status', 'Status')}</TableCell>
                    <TableCell>{t('customers.columns.actions', 'Actions')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredCustomers
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((customer) => (
                    <TableRow 
                      key={customer.id} 
                      hover 
                      onClick={() => setSelectedCustomer(customer)}
                      selected={selectedCustomer?.id === customer.id}
                      sx={{ cursor: 'pointer' }}
                    >
                      <TableCell>{customer.id}</TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Avatar sx={{ width: 32, height: 32, bgcolor: theme.palette.primary.main }}>
                            {customer.name?.charAt(0) || '?'}
                          </Avatar>
                          <Typography variant="body2">{customer.name}</Typography>
                        </Box>
                      </TableCell>
                      <TableCell>{customer.email}</TableCell>
                      <TableCell>{customer.company_name || customer.company}</TableCell>
                      <TableCell>
                        <Chip 
                          label={t(`customers.status.${customer.status}`, customer.status)} 
                          size="small" 
                          color={getStatusColor(customer.status)}
                        />
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                          <Tooltip title={t('common.view', 'View')}>
                            <IconButton size="small" onClick={(e) => { e.stopPropagation(); setSelectedCustomer(customer); }}>
                              <ViewIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title={t('common.edit', 'Edit')}>
                            <IconButton size="small" onClick={(e) => { e.stopPropagation(); setSelectedCustomer(customer); handleOpenDialog('edit', customer); }}>
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title={t('common.delete', 'Delete')}>
                            <IconButton size="small" color="error" onClick={(e) => { e.stopPropagation(); setSelectedCustomer(customer); setDeleteConfirmOpen(true); }}>
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                  {filteredCustomers.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={6} align="center">
                        <Typography variant="body2" color="text.secondary">
                          {t('customers.noCustomers', 'No customers found')}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
              <TablePagination
                rowsPerPageOptions={[5, 10, 25]}
                component="div"
                count={filteredCustomers.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={(e, newPage) => setPage(newPage)}
                onRowsPerPageChange={(e) => {
                  setRowsPerPage(parseInt(e.target.value, 10));
                  setPage(0);
                }}
              />
            </TableContainer>
          )}
        </Box>

        {selectedCustomer && (
          <Box sx={{ width: '40%', p: 2, overflow: 'auto' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">{t('customers.details', 'Customer Details')}</Typography>
              <IconButton size="small" onClick={() => setSelectedCustomer(null)}>
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Box>

            {customerDetailLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                <CircularProgress />
              </Box>
            ) : (
              <Stack spacing={2}>
                <Card variant="outlined">
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                      <Avatar sx={{ width: 64, height: 64, bgcolor: theme.palette.primary.main, fontSize: '1.5rem' }}>
                        {selectedCustomer.name?.charAt(0) || '?'}
                      </Avatar>
                      <Box>
                        <Typography variant="h6">{selectedCustomer.name}</Typography>
                        <Chip 
                          label={t(`customers.status.${selectedCustomer.status}`, selectedCustomer.status)} 
                          size="small" 
                          color={getStatusColor(selectedCustomer.status)}
                        />
                      </Box>
                    </Box>
                    <Divider sx={{ my: 2 }} />
                    <Grid container spacing={2}>
                      <Grid item xs={12}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <EmailIcon color="action" fontSize="small" />
                          <Typography variant="body2">{selectedCustomer.email || '-'}</Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={12}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <PhoneIcon color="action" fontSize="small" />
                          <Typography variant="body2">{selectedCustomer.phone || '-'}</Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={12}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <BusinessIcon color="action" fontSize="small" />
                          <Typography variant="body2">{selectedCustomer.company || '-'}</Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={12}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <LocationIcon color="action" fontSize="small" />
                          <Typography variant="body2">
                            {[selectedCustomer.address, selectedCustomer.city, selectedCustomer.country].filter(Boolean).join(', ') || '-'}
                          </Typography>
                        </Box>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>

                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="subtitle2" gutterBottom>
                      <MoneyIcon sx={{ mr: 1, verticalAlign: 'middle' }} fontSize="small" />
                      {t('customers.pricingGroup', 'Pricing Group')}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <Typography variant="body2">
                        {customerPricingGroup?.name || t('customers.noPricingGroup', 'No pricing group assigned')}
                      </Typography>
                      <Button size="small" startIcon={<AssignmentIcon />} onClick={() => setAssignPricingOpen(true)}>
                        {t('customers.assignPricing', 'Assign')}
                      </Button>
                    </Box>
                  </CardContent>
                </Card>

                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button 
                    fullWidth 
                    variant="outlined" 
                    startIcon={<EditIcon />}
                    onClick={() => handleOpenDialog('edit', selectedCustomer)}
                  >
                    {t('common.edit', 'Edit')}
                  </Button>
                  <Button 
                    fullWidth 
                    variant="outlined" 
                    color="error"
                    startIcon={<DeleteIcon />}
                    onClick={() => setDeleteConfirmOpen(true)}
                  >
                    {t('common.delete', 'Delete')}
                  </Button>
                </Box>
              </Stack>
            )}
          </Box>
        )}
      </Box>

      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {dialogMode === 'create' 
            ? t('customers.addCustomer', 'Add Customer') 
            : t('customers.editCustomer', 'Edit Customer')}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label={t('customers.fields.name', 'Name')}
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label={t('customers.fields.email', 'Email')}
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label={t('customers.fields.phone', 'Phone')}
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label={t('customers.fields.company', 'Company')}
                value={formData.company}
                onChange={(e) => setFormData({ ...formData, company: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label={t('customers.fields.address', 'Address')}
                value={formData.address}
                onChange={(e) => setFormData({ ...formData, address: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label={t('customers.fields.city', 'City')}
                value={formData.city}
                onChange={(e) => setFormData({ ...formData, city: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label={t('customers.fields.country', 'Country')}
                value={formData.country}
                onChange={(e) => setFormData({ ...formData, country: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>{t('customers.fields.status', 'Status')}</InputLabel>
                <Select
                  value={formData.status}
                  label={t('customers.fields.status', 'Status')}
                  onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                >
                  <MenuItem value="active">{t('customers.status.active', 'Active')}</MenuItem>
                  <MenuItem value="inactive">{t('customers.status.inactive', 'Inactive')}</MenuItem>
                  <MenuItem value="suspended">{t('customers.status.suspended', 'Suspended')}</MenuItem>
                  <MenuItem value="pending">{t('customers.status.pending', 'Pending')}</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>{t('common.cancel', 'Cancel')}</Button>
          <Button 
            variant="contained" 
            onClick={handleSubmit}
            disabled={createCustomer.isPending || updateCustomer.isPending}
          >
            {dialogMode === 'create' ? t('common.create', 'Create') : t('common.save', 'Save')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={deleteConfirmOpen} onClose={() => setDeleteConfirmOpen(false)}>
        <DialogTitle>{t('customers.deleteConfirmTitle', 'Delete Customer')}</DialogTitle>
        <DialogContent>
          <Typography>
            {t('customers.deleteConfirmMessage', 'Are you sure you want to delete this customer? This action cannot be undone.')}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmOpen(false)}>{t('common.cancel', 'Cancel')}</Button>
          <Button 
            variant="contained" 
            color="error" 
            onClick={handleDelete}
            disabled={deleteCustomer.isPending}
          >
            {t('common.delete', 'Delete')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={assignPricingOpen} onClose={() => setAssignPricingOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{t('customers.assignPricingTitle', 'Assign Pricing Group')}</DialogTitle>
        <DialogContent>
          <FormControl fullWidth sx={{ mt: 2 }}>
            <InputLabel>{t('customers.selectPricingGroup', 'Select Pricing Group')}</InputLabel>
            <Select
              value={selectedPricingGroup}
              label={t('customers.selectPricingGroup', 'Select Pricing Group')}
              onChange={(e) => setSelectedPricingGroup(e.target.value)}
            >
              {pricingGroups.map(group => (
                <MenuItem key={group.id} value={group.id}>
                  {group.name} - {group.tier}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAssignPricingOpen(false)}>{t('common.cancel', 'Cancel')}</Button>
          <Button 
            variant="contained" 
            onClick={handleAssignPricingGroup}
            disabled={!selectedPricingGroup || assignPricingGroup.isPending}
          >
            {t('common.assign', 'Assign')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default React.memo(CustomerManagementPanel);
